// Import debounce
import debounce from 'debounce';

/**
 * TEMPORARY wrapper for the fields until we move to web components
 */
import Field from '@automator/builder/_legacy/js/field';
import { sanitizeFieldAttributes } from '@automator/builder/_legacy/js/field/utilities';

// Import dependencies
import { html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

// Import utils
import { isDefined } from '@automator/shared/utils/is-defined';
import { isEmpty } from '@automator/shared/utils/is-empty';

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-builder-item-legacy-form' )
export class AutomatorBuilderItemLegacyForm extends LitElement {
	// The post ID of the parent item linked to the field
	@property( { type: Number, attribute: 'parent-id' } ) accessor parentID;

	// The post ID of the item linked to the field
	@property( { type: Number, attribute: 'item-id' } ) accessor itemID;

	// The type of the item linked to the field
	@property( { type: String, attribute: 'item-type' } ) accessor itemType;

	// Array that contains the fields to render, including the attributes
	@property( { type: Array, attribute: false } ) accessor fieldsDirectory;

	// The fields values
	@property( { type: Array, attribute: false } ) accessor fieldsValues;

	// The fields values
	@property( { type: String, attribute: 'return-format' } ) accessor returnFormat = 'legacy-meta';

	/**
	 * Disable the Shadow DOM
	 *
	 * @todo Remove this once we move fields to web components
	 * @return {HTMLElement} The component
	 */
	createRenderRoot() {
		return this;
	}

	// eslint-disable-next-line require-jsdoc
	render() {
		return html`
			<div class="uap uap-legacy-form">
				${
					repeat(
						// Get integrations with conditions
						Object.values( this.fields ),
						( field ) => field.id,
						field => {
							// Listen changes to the field
							field.formElement
								.off( 'automator:field:change.legacy-form' )
								.on(
									'automator:field:change.legacy-form',
									debounce( this.triggerChangeEvent.bind( this ) )
								);

							return html`${ field.$wrapper.get( 0 ) }`
						}
					)
				}
			</div>
		`;
	}

	/**
	 * Triggers a change event on the component.
	 *
	 * This method is called in response to a change in any of the component's fields. When invoked, it
	 * dispatches a 'uap-builder-legacy-form-change' event with a detail object containing the current
	 * unsaved field values.
	 *
	 * Note: This method is implemented as an arrow function to automatically bind the context (`this`) to
	 * the current class instance. This ensures that `this` inside the method always refers to the class instance,
	 * even when the method is passed around or used as a callback.
	 */
	triggerChangeEvent = () => {
		this.dispatchEvent( new CustomEvent(
			'uap-builder-legacy-form-change',
			{
				detail: {
					unsavedFieldValues: this.unsavedFieldValues
				}
			}
		) );
	}

	/**
	 * Returns an object with the instances of the fields
	 *
	 * @return {Object} Object of Field instances
	 */
	get fields() {
		// Check if we already created the Field instances
		if ( isDefined( this._fields ) ) {
			return this._fields;
		}

		// Check if we already have the condition fields in the post data
		if ( ! isDefined( this.fieldsDirectory ) ) {
			return null;
		}

		// Iterate the fields in the post data
		this._fields = {};

		this.fieldsDirectory.forEach( fieldAttributes => {
			// Clone the attributes before doing modifications to them
			const fieldAttrs = Object.assign( {
				fieldType: fieldAttributes.type,
				supportsTokens: true,
				itemType: this.itemType,
			}, sanitizeFieldAttributes( fieldAttributes ) );

			fieldAttrs.itemId = this.itemID;
			fieldAttrs.parentID = this.parentID;

			// Get the field value, if it exists
			// @to-do - Check if we need to use option_code or optionCode
			const fieldValue = this.fieldsValues?.[ ( fieldAttributes.option_code || fieldAttributes.optionCode ) ];

			// Check if there is a value to prepopulate this field
			if ( isDefined( fieldValue ) ) {
				// Set current value
				fieldAttrs.currentValue = fieldValue.value;

				// Check if it's a custom select
				if ( fieldAttrs.currentValue === 'automator_custom_value' ) {
					fieldAttrs.customValue = fieldValue.custom;
				}
			}

			// Create field instance
			const field = new Field( fieldAttrs.fieldType, fieldAttrs, false );

			// Save field instance
			this._fields[ fieldAttrs.optionCode ] = field;
		} );

		// Once all the fields are defined, iterate fields and create the DOM elements
		Object.entries( this._fields ).forEach( ( [ fieldCode, field ] ) => {
			field.createFormElement();

			// Init libraries
			// Initilize the token selector
			field.initTokenSelector();
			// Initilize flatpickr
			field.initFlatpickr();
			// Initilize CodeMirror
			field.initCodeMirror();
		} );

		return this._fields;
	}

	/**
	 * Returns the values of the fields inside this component
	 *
	 * @return {Object} The values of the fields
	 */
	get unsavedFieldValues() {
		const fieldValues = {}

		Object.entries( this.fields ).forEach( ( [ fieldCode, field ] ) => {
			// If the field is required and doesn't have a value, don't add it
			if ( field.attributes.isRequired && isEmpty( field.value ) ) {
				return;
			}

			if ( [ 'loop', 'token', 'standard' ].includes( this.returnFormat ) ) {
				fieldValues[ fieldCode ] = field.valueStandardFormat;
			}
		} );

		return fieldValues;
	}

	/**
	 * Validates the fields
	 *
	 * @return {Boolean} True if the fields are valid
	 */
	get areFieldsValid() {
		return Object.values( this.fields ).every( field => field.validate().isValid );
	}
}
